/**
 * UpdateProfile — mobile-parity edit screen: avatar with camera badge,
 * underline Name/Bio fields, four tappable edit cards (Gender, Country,
 * Weight, DOB) each opening a modal with a picker + privacy selector,
 * and a sticky Save button. Saving goes through ProfileContext.updateProfile
 * so the mapper owns the API shape.
 */
import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { COUNTRIES, countryCodeToFlag } from '@wlb/shared/constants/countries';
import {
  PRIVACY_ID_TO_FIELD_VISIBILITY,
  PRIVACY_LEVEL,
  toPrivacyLevel,
  type PrivacyLevel,
} from '@wlb/shared/constants/privacy';
import { PROFILE_INFO_LIMITS } from '@wlb/shared/api/onboarding/onboardingErrors';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  formatWeightInUnit,
  normalizeWeightUnit,
  calculateWeightProgress,
  validateProfileWeights,
  WEIGHT_UNIT,
  type WeightUnitCode,
} from '@wlb/shared/utils/weightUtils';
import {
  formatHeightFromCm,
  fromHeightCM,
  normalizeHeightUnit,
  toHeightCM,
} from '@wlb/shared/utils/heightUtils';
import {
  formatDateOfBirth,
  parseDateOnlyString,
  toDateOnlyString,
} from '@wlb/shared/utils/dateHelpers';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { FormError } from '../components/ui/FormError';
import { Modal } from '../components/ui/Modal';
import { ScrollDatePicker } from '../components/ui/ScrollDatePicker';
import { MAX_DOB_YEAR, MIN_DOB_YEAR } from '../features/auth/dobCalendar';
import {
  BuddiesIcon,
  CalendarIcon,
  CameraIcon,
  ChevronLeftIcon,
  GlobeIcon,
  LockIcon,
  MapPinIcon,
  PencilIcon,
  RulerIcon,
  SearchIcon,
  UserIcon,
  WeightScaleIcon,
  type IconProps,
} from '../components/ui/icons';
import { getProfileAvatarUrl } from '../features/profile/useCurrentProfile';
import { AvatarUploadModal } from '../features/profile/AvatarUploadModal';
import {
  convertProfileWeightFields,
  sanitizeWeightInput,
  type ProfileWeightFields,
} from '../features/profile/profileWeightFields';
import '../features/profile/profile.css';
import '../features/profile/updateProfile.css';

const MIN_AGE = PROFILE_INFO_LIMITS.MIN_AGE_YEARS;
const { min: MIN_HEIGHT_CM, max: MAX_HEIGHT_CM } = PROFILE_INFO_LIMITS.HEIGHT_CM;
const BIO_MAX = 250;

/** Trims conversion noise so a limit reads as "3 m" / "9.84 ft". */
const round2 = (value: number): number => Number(value.toFixed(2));

/** Height is typed in metres or decimal feet; the API only ever sees cm. */
const heightUnitKey = (unit: string): string =>
  unit === 'ft' ? 'basicInfo.unitShortFeet' : 'basicInfo.unitShortMeters';

const GENDERS = [
  ['Male', 'profile.gender.options.male'],
  ['Female', 'profile.gender.options.female'],
  ['Other', 'profile.gender.options.other'],
] as const;

type FieldName = 'bio' | 'weight' | 'height' | 'dob';
type FieldErrors = Partial<Record<FieldName, string>>;
type EditModal = 'gender' | 'country' | 'weight' | 'height' | 'dob' | null;
type WeightTab = keyof ProfileWeightFields;

interface PrivacyOption {
  level: PrivacyLevel;
  icon: ComponentType<IconProps>;
  titleKey: string;
  descKey: string;
}

const PRIVACY_OPTIONS: PrivacyOption[] = [
  { level: PRIVACY_LEVEL.PUBLIC, icon: GlobeIcon, titleKey: 'profile.privacy.options.public.title', descKey: 'profile.privacy.options.public.description' },
  { level: PRIVACY_LEVEL.BUDDIES, icon: BuddiesIcon, titleKey: 'profile.privacy.options.buddies.title', descKey: 'profile.privacy.options.buddies.description' },
  { level: PRIVACY_LEVEL.PRIVATE, icon: LockIcon, titleKey: 'profile.privacy.options.private.title', descKey: 'profile.privacy.options.private.description' },
];

function EditCard({
  icon,
  label,
  value,
  ariaLabel,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="profile-edit__card" onClick={onClick} aria-label={ariaLabel}>
      <span className="profile-edit__card-icon" aria-hidden>{icon}</span>
      <span className="profile-edit__card-text">
        <span className="profile-edit__card-label">{label}</span>
        <span className="profile-edit__card-value">{value}</span>
      </span>
      <PencilIcon size={20} className="profile-edit__card-pencil" />
    </button>
  );
}

function PrivacySelector({
  value,
  onChange,
}: {
  value: PrivacyLevel;
  onChange: (level: PrivacyLevel) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="profile-edit__privacy">
      <span className="profile-edit__privacy-heading">{t('profile.privacy.chooseAudience')}</span>
      {PRIVACY_OPTIONS.map(option => {
        const Icon = option.icon;
        return (
          <button
            key={option.level}
            type="button"
            className={`profile-edit__privacy-option${value === option.level ? ' profile-edit__privacy-option--selected' : ''}`}
            onClick={() => onChange(option.level)}
            aria-pressed={value === option.level}
          >
            <span className="profile-edit__privacy-icon" aria-hidden><Icon size={20} /></span>
            <span className="profile-edit__privacy-text">
              <span className="profile-edit__privacy-title">{t(option.titleKey)}</span>
              <span className="profile-edit__privacy-desc">{t(option.descKey)}</span>
            </span>
            <span className="profile-edit__privacy-radio" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

export function UpdateProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();

  const [bio, setBio] = useState(profile?.bio ?? '');
  const [gender, setGender] = useState(profile?.gender ?? 'Male');
  const [country, setCountry] = useState(profile?.country ?? '');
  const [startWeight, setStartWeight] = useState(
    profile?.startWeight != null ? String(profile.startWeight) : '',
  );
  const [currentWeight, setCurrentWeight] = useState(
    profile?.currentWeight != null ? String(profile.currentWeight) : '',
  );
  const [goalWeight, setGoalWeight] = useState(
    profile?.goalWeight != null ? String(profile.goalWeight) : '',
  );
  const [unit, setUnit] = useState(normalizeWeightUnit(profile?.unit));
  const [weightTab, setWeightTab] = useState<WeightTab>('current');
  const [heightUnit, setHeightUnit] = useState(() => normalizeHeightUnit(profile?.heightUnit));
  const [height, setHeight] = useState(() =>
    profile?.heightCm
      ? String(round2(fromHeightCM(profile.heightCm, normalizeHeightUnit(profile.heightUnit))))
      : '',
  );
  const [dob, setDob] = useState(profile?.dateOfBirth?.slice(0, 10) ?? '');
  const [countryLevel, setCountryLevel] = useState(
    toPrivacyLevel(profile?.countryPrivacy) ?? PRIVACY_LEVEL.PUBLIC,
  );
  const [weightLevel, setWeightLevel] = useState(
    toPrivacyLevel(profile?.weightPrivacy) ?? PRIVACY_LEVEL.PUBLIC,
  );
  const [heightLevel, setHeightLevel] = useState(
    toPrivacyLevel(profile?.heightPrivacy) ?? PRIVACY_LEVEL.PUBLIC,
  );
  const [dobLevel, setDobLevel] = useState(
    toPrivacyLevel(profile?.dateOfBirthPrivacy) ?? PRIVACY_LEVEL.PUBLIC,
  );

  const [errors, setErrors] = useState<FieldErrors>({});
  const [saveFailed, setSaveFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeModal, setActiveModal] = useState<EditModal>(null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const avatarUrl = getProfileAvatarUrl(profile);

  const filteredCountries = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    return query
      ? COUNTRIES.filter(c => c.label.toLowerCase().includes(query))
      : COUNTRIES;
  }, [countrySearch]);

  const countryLabel = useMemo(() => {
    const found = COUNTRIES.find(
      c => c.label === country || c.value === country || String(c.id) === country,
    );
    return found?.label ?? country;
  }, [country]);

  const weightFields = useMemo<ProfileWeightFields>(
    () => ({ start: startWeight, current: currentWeight, goal: goalWeight }),
    [startWeight, currentWeight, goalWeight],
  );

  const formatWeight = (value: string): string => {
    const numericValue = Number(value);
    return value && Number.isFinite(numericValue)
      ? formatWeightInUnit(numericValue, unit)
      : '--';
  };

  const weightDisplay = [
    `${t('profile.weightUpdate.summaryLabels.start')}: ${formatWeight(startWeight)}`,
    `${t('profile.weightUpdate.summaryLabels.current')}: ${formatWeight(currentWeight)}`,
    `${t('profile.weightUpdate.summaryLabels.goal')}: ${formatWeight(goalWeight)}`,
  ].join(' · ');

  const weightProgress = useMemo(() => {
    const start = Number(startWeight) || 0;
    const current = Number(currentWeight) || 0;
    const goal = Number(goalWeight) || 0;
    const percent = calculateWeightProgress(start, current, goal);
    return {
      start: start ? formatWeightInUnit(start, unit) : '--',
      current: current ? formatWeightInUnit(current, unit) : '--',
      goal: goal ? formatWeightInUnit(goal, unit) : '--',
      percent,
    };
  }, [startWeight, currentWeight, goalWeight, unit]);

  const activeWeight = weightFields[weightTab];
  const activeWeightLabel = t(`profile.updateProfile.labels.${weightTab}Weight`);

  const updateActiveWeight = (value: string): void => {
    const sanitized = sanitizeWeightInput(value);
    if (weightTab === 'start') setStartWeight(sanitized);
    else if (weightTab === 'current') setCurrentWeight(sanitized);
    else setGoalWeight(sanitized);
    clearError('weight');
  };

  const changeWeightUnit = (nextUnit: WeightUnitCode): void => {
    const converted = convertProfileWeightFields(weightFields, unit, nextUnit);
    setStartWeight(converted.start);
    setCurrentWeight(converted.current);
    setGoalWeight(converted.goal);
    setUnit(nextUnit);
    clearError('weight');
  };

  /** The API field is always centimetres, whatever unit is on screen. */
  const heightCm = useMemo(() => {
    const numeric = Number(height);
    return height && Number.isFinite(numeric) && numeric > 0
      ? round2(toHeightCM(numeric, heightUnit))
      : null;
  }, [height, heightUnit]);

  const heightDisplay = heightCm != null ? formatHeightFromCm(heightCm, heightUnit) : '--';

  /** Re-expresses the entered value in the newly picked unit. */
  const changeHeightUnit = (next: 'm' | 'ft'): void => {
    setHeight(value =>
      value && Number.isFinite(Number(value))
        ? String(round2(fromHeightCM(toHeightCM(Number(value), heightUnit), next)))
        : value,
    );
    setHeightUnit(next);
    clearError('height');
  };

  const dobDisplay = useMemo(() => (dob ? formatDateOfBirth(dob) : '--'), [dob]);

  /**
   * Bounds for the date wheel: no younger than the minimum age, and no further
   * back than the picker's oldest year.
   */
  const minDob = useMemo(() => new Date(MIN_DOB_YEAR, 0, 1), []);
  const maxDob = useMemo(() => {
    const d = new Date();
    return new Date(MAX_DOB_YEAR, d.getMonth(), d.getDate());
  }, []);

  /**
   * The wheel works in `Date`, the request in `yyyy-mm-dd`. A profile with no
   * date on file opens on the newest date it is allowed to offer.
   */
  const dobValue = useMemo(() => {
    const parsed = dob ? parseDateOnlyString(dob) : null;
    return parsed ?? maxDob;
  }, [dob, maxDob]);

  /**
   * The wheel turns a draft, not the field: only Done writes the date back, so
   * dismissing the modal with Escape, the backdrop or × leaves the form as it
   * was. Null means the modal is closed.
   */
  const [dobDraft, setDobDraft] = useState<Date | null>(null);

  const openDobModal = (): void => {
    setDobDraft(dobValue);
    setActiveModal('dob');
  };

  const closeDobModal = (): void => {
    setDobDraft(null);
    setActiveModal(null);
  };

  const confirmDob = (): void => {
    if (dobDraft) setDob(toDateOnlyString(dobDraft));
    closeDobModal();
  };

  const hasChanges = useMemo(() => {
    if (!profile) return false;
    const origUnit = normalizeWeightUnit(profile.unit);
    const origCountryLevel = toPrivacyLevel(profile.countryPrivacy) ?? PRIVACY_LEVEL.PUBLIC;
    const origWeightLevel = toPrivacyLevel(profile.weightPrivacy) ?? PRIVACY_LEVEL.PUBLIC;
    const origDobLevel = toPrivacyLevel(profile.dateOfBirthPrivacy) ?? PRIVACY_LEVEL.PUBLIC;
    const origHeightLevel = toPrivacyLevel(profile.heightPrivacy) ?? PRIVACY_LEVEL.PUBLIC;
    const origHeightUnit = normalizeHeightUnit(profile.heightUnit);
    return (
      heightCm !== (profile.heightCm != null ? round2(profile.heightCm) : null) ||
      heightUnit !== origHeightUnit ||
      heightLevel !== origHeightLevel ||
      bio.trim() !== (profile.bio ?? '') ||
      gender !== (profile.gender ?? 'Male') ||
      country !== (profile.country ?? '') ||
      Number(startWeight) !== Number(profile.startWeight ?? 0) ||
      Number(currentWeight) !== Number(profile.currentWeight ?? 0) ||
      Number(goalWeight) !== Number(profile.goalWeight ?? 0) ||
      unit !== origUnit ||
      dob !== (profile.dateOfBirth?.slice(0, 10) ?? '') ||
      countryLevel !== origCountryLevel ||
      weightLevel !== origWeightLevel ||
      dobLevel !== origDobLevel
    );
  }, [profile, bio, gender, country, startWeight, currentWeight, goalWeight, unit, heightCm, heightUnit, dob, countryLevel, weightLevel, heightLevel, dobLevel]);

  const clearError = (field: FieldName) =>
    setErrors(current => (current[field] ? { ...current, [field]: undefined } : current));

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};

    if (bio.trim().length > BIO_MAX) next.bio = t('profile.updateProfile.errors.bioTooLong');

    const weightValidation = validateProfileWeights({
      startWeight: startWeight ? Number(startWeight) : undefined,
      startWeightUnit: unit,
      currentWeight: currentWeight ? Number(currentWeight) : null,
      goalWeight: goalWeight ? Number(goalWeight) : undefined,
      goalWeightUnit: unit,
      unit,
      t,
    });
    if (!weightValidation.isValid) {
      next.weight =
        weightValidation.combinedErrorMessage
        ?? t('profile.updateProfile.errors.weightInvalid');
    }

    // Height stays optional here, but a typed value must clear the API bounds.
    if (height) {
      const numeric = Number(height);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        next.height = t('basicInfo.errors.heightInvalid');
      } else {
        const cm = toHeightCM(numeric, heightUnit);
        if (cm <= MIN_HEIGHT_CM || cm > MAX_HEIGHT_CM) {
          next.height = t('basicInfo.errors.heightOutOfRange', {
            max: round2(fromHeightCM(MAX_HEIGHT_CM, heightUnit)),
            unit: t(heightUnitKey(heightUnit)),
          });
        }
      }
    }

    if (dob) {
      const parsed = new Date(dob);
      const now = new Date();
      const latestAllowed = new Date(now.getFullYear() - MIN_AGE, now.getMonth(), now.getDate());
      if (Number.isNaN(parsed.getTime()) || parsed > latestAllowed) {
        next.dob = t('profile.updateProfile.errors.dobInvalid', { age: MIN_AGE });
      }
    }

    return next;
  };

  const handleSave = async () => {
    const found = validate();
    setErrors(found);
    setSaveFailed(false);
    if (Object.values(found).some(Boolean)) return;

    setIsSaving(true);
    try {
      await updateProfile({
        bio: bio.trim(),
        gender,
        country,
        startWeight: startWeight ? Number(startWeight) : undefined,
        startWeightUnit: startWeight ? unit : undefined,
        currentWeight: Number(currentWeight),
        goalWeight: goalWeight ? Number(goalWeight) : undefined,
        goalWeightUnit: goalWeight ? unit : undefined,
        unit: unit === 'kg' ? WEIGHT_UNIT.KG : WEIGHT_UNIT.LBS,
        // Omitted entirely when unset so the mapper does not send a 0 height.
        ...(heightCm != null ? { heightCm, heightUnit } : {}),
        dateOfBirth: dob || undefined,
        countryPrivacy: PRIVACY_ID_TO_FIELD_VISIBILITY[countryLevel],
        weightPrivacy: PRIVACY_ID_TO_FIELD_VISIBILITY[weightLevel],
        heightPrivacy: PRIVACY_ID_TO_FIELD_VISIBILITY[heightLevel],
        dateOfBirthPrivacy: PRIVACY_ID_TO_FIELD_VISIBILITY[dobLevel],
      });
      navigate('/profile');
    } catch {
      setSaveFailed(true);
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = (error?: string): string =>
    `profile-edit__input${error ? ' profile-edit__input--error' : ''}`;

  return (
    <section className="profile-edit-page" aria-label={t('profile.updateProfile.title')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('profile.accessibility.back')}
          onClick={() => navigate('/profile')}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('profile.updateProfile.title')}</h1>
      </div>

      <div className="profile-edit__body">
        <FormError variant="banner" message={saveFailed && t('profile.updateProfile.errors.saveFailed')} />

        {/* Avatar */}
        <div className="profile-edit__avatar-section">
          <div className="profile-edit__avatar-wrapper">
            <Avatar
              src={avatarUrl}
              name={profile?.userName}
              username={profile?.userName}
              seed={profile?.userGuid ?? profile?.userId}
              size="xl"
              className="profile-edit__avatar-img"
            />
            <button
              type="button"
              className="profile-edit__camera-badge"
              aria-label={t('profile.accessibility.changePhoto')}
              onClick={() => setAvatarModalOpen(true)}
            >
              <CameraIcon size={16} />
            </button>
          </div>
        </div>

        {/* Name */}
        {/* Bio */}
        <label className="profile-edit__field">
          <span className="profile-edit__field-label">{t('profile.updateProfile.labels.bio')}</span>
          <textarea
            className={`${inputClass(errors.bio)} profile-edit__textarea`}
            value={bio}
            rows={3}
            maxLength={BIO_MAX}
            placeholder={t('profile.updateProfile.bioPlaceholder')}
            aria-invalid={Boolean(errors.bio)}
            aria-describedby={errors.bio ? 'update-profile-bio-error' : undefined}
            onChange={event => { setBio(event.target.value); clearError('bio'); }}
          />
          <FormError message={errors.bio} id="update-profile-bio-error" />
        </label>

        {/* Edit cards */}
        <div className="profile-edit__cards">
          <EditCard
            icon={<UserIcon size={24} />}
            label={t('profile.updateProfile.labels.gender')}
            value={gender || '--'}
            ariaLabel={t('profile.updateProfile.accessibility.editField', { field: t('profile.updateProfile.labels.gender') })}
            onClick={() => setActiveModal('gender')}
          />
          <EditCard
            icon={<MapPinIcon size={24} />}
            label={t('profile.updateProfile.labels.country')}
            value={countryLabel || '--'}
            ariaLabel={t('profile.updateProfile.accessibility.editField', { field: t('profile.updateProfile.labels.country') })}
            onClick={() => setActiveModal('country')}
          />
          <EditCard
            icon={<WeightScaleIcon size={24} />}
            label={t('profile.weightUpdate.title')}
            value={weightDisplay}
            ariaLabel={t('profile.updateProfile.accessibility.editField', { field: t('profile.weightUpdate.title') })}
            onClick={() => {
              setWeightTab('current');
              setActiveModal('weight');
            }}
          />
          {activeModal !== 'weight' && (
            <FormError message={errors.weight} id="update-profile-weight-error" />
          )}
          <EditCard
            icon={<RulerIcon size={24} />}
            label={t('profile.updateProfile.labels.height')}
            value={heightDisplay}
            ariaLabel={t('profile.updateProfile.accessibility.editField', { field: t('profile.updateProfile.labels.height') })}
            onClick={() => setActiveModal('height')}
          />
          {activeModal !== 'height' && (
            <FormError message={errors.height} id="update-profile-height-error" />
          )}
          <EditCard
            icon={<CalendarIcon size={24} />}
            label={t('profile.updateProfile.labels.dateOfBirth')}
            value={dobDisplay}
            ariaLabel={t('profile.updateProfile.accessibility.editField', { field: t('profile.updateProfile.labels.dateOfBirth') })}
            onClick={openDobModal}
          />
        </div>
      </div>

      {/* Weight progress card */}
      <div className="profile-edit__weight-card">
        <h3 className="profile-edit__weight-title">{t('profile.weightProgress.title')}</h3>
        <p className="profile-edit__weight-subtitle">{t('profile.weightProgress.subtitle')}</p>
        <div className="profile-edit__weight-row">
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--start" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.start')}</span>
            <span className="profile-edit__weight-value">{weightProgress.start}</span>
          </div>
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--current" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.current')}</span>
            <span className="profile-edit__weight-value">{weightProgress.current}</span>
          </div>
          <div className="profile-edit__weight-col">
            <span className="profile-edit__weight-dot profile-edit__weight-dot--goal" aria-hidden />
            <span className="profile-edit__weight-label">{t('profile.weightProgress.goal')}</span>
            <span className="profile-edit__weight-value">{weightProgress.goal}</span>
          </div>
        </div>
        <div className="profile-edit__progress-header">
          <span className="profile-edit__progress-label">{t('profile.weightProgress.progressLabel')}</span>
          <span
            className={`profile-edit__progress-percent${weightProgress.percent >= 100 ? ' profile-edit__progress-percent--done' : ''}`}
          >
            {Math.round(weightProgress.percent)}%
          </span>
        </div>
        <div
          className="profile-edit__progress-bar"
          role="progressbar"
          aria-valuenow={Math.round(weightProgress.percent)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="profile-edit__progress-fill"
            style={{ width: `${Math.min(weightProgress.percent, 100)}%` }}
          />
        </div>
      </div>

      {/* Sticky save */}
      <div className="profile-edit__save-bar">
        <Button
          fullWidth
          onClick={() => { handleSave().catch(() => undefined); }}
          disabled={!hasChanges || isSaving}
        >
          {t(isSaving ? 'profile.updateProfile.saving' : 'common.buttons.save')}
        </Button>
      </div>

      {/* Avatar upload modal */}
      {avatarModalOpen && (
        <AvatarUploadModal
          currentAvatarUrl={avatarUrl}
          onClose={() => setAvatarModalOpen(false)}
        />
      )}

      {/* Gender modal */}
      {activeModal === 'gender' && (
        <Modal
          title={t('profile.gender.title')}
          labelledBy="edit-gender-title"
          onClose={() => setActiveModal(null)}
        >
          <div className="profile-edit__modal-body">
            <div className="profile-edit__gender-row">
              {GENDERS.map(([value, labelKey]) => (
                <button
                  key={value}
                  type="button"
                  className={`profile-edit__gender-chip${gender === value ? ' profile-edit__gender-chip--selected' : ''}`}
                  onClick={() => setGender(value)}
                  aria-pressed={gender === value}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div className="profile-edit__modal-footer">
            <Button fullWidth onClick={() => setActiveModal(null)}>
              {t('common.buttons.done')}
            </Button>
          </div>
        </Modal>
      )}

      {/* Country modal */}
      {activeModal === 'country' && (
        <Modal
          title={t('profile.country.title')}
          labelledBy="edit-country-title"
          onClose={() => setActiveModal(null)}
        >
          <div className="profile-edit__modal-body">
            <label className="profile-edit__country-search">
              <span className="sr-only">{t('profile.country.searchPlaceholder')}</span>
              <SearchIcon size={16} />
              <input
                value={countrySearch}
                onChange={event => setCountrySearch(event.target.value)}
                placeholder={t('profile.country.searchPlaceholder')}
                autoFocus
              />
            </label>
            <div className="profile-edit__country-list">
              {filteredCountries.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`profile-edit__country-option${c.label === country ? ' profile-edit__country-option--selected' : ''}`}
                  onClick={() => setCountry(c.label)}
                  aria-pressed={c.label === country}
                >
                  <span>{countryCodeToFlag(c.value)} {c.label}</span>
                  {c.label === country && <span aria-hidden>✓</span>}
                </button>
              ))}
            </div>
            <PrivacySelector value={countryLevel} onChange={setCountryLevel} />
          </div>
          <div className="profile-edit__modal-footer">
            <Button fullWidth onClick={() => setActiveModal(null)}>
              {t('common.buttons.done')}
            </Button>
          </div>
        </Modal>
      )}

      {/* Weight modal */}
      {activeModal === 'weight' && (
        <Modal
          title={t('profile.weightUpdate.title')}
          labelledBy="edit-weight-title"
          onClose={() => setActiveModal(null)}
        >
          <div className="profile-edit__modal-body">
            <div
              className="profile-edit__weight-tabs"
              role="tablist"
              aria-label={t('profile.weightUpdate.title')}
            >
              {(['start', 'current', 'goal'] as const).map(tab => (
                <button
                  key={tab}
                  id={`profile-weight-tab-${tab}`}
                  type="button"
                  role="tab"
                  className={`profile-edit__weight-tab${weightTab === tab ? ' profile-edit__weight-tab--selected' : ''}`}
                  aria-selected={weightTab === tab}
                  aria-controls="profile-weight-panel"
                  onClick={() => setWeightTab(tab)}
                >
                  {t(`profile.weightProgress.${tab}`)}
                </button>
              ))}
            </div>
            <div
              id="profile-weight-panel"
              role="tabpanel"
              aria-labelledby={`profile-weight-tab-${weightTab}`}
            >
              <div className="profile-edit__weight-row">
                <input
                  className="profile-edit__weight-input"
                  value={activeWeight}
                  inputMode="decimal"
                  aria-label={activeWeightLabel}
                  aria-invalid={Boolean(errors.weight)}
                  aria-describedby={errors.weight ? 'update-profile-weight-error' : undefined}
                  onChange={event => updateActiveWeight(event.target.value)}
                />
                <div className="profile-edit__unit-toggle">
                  <button
                    type="button"
                    className={`profile-edit__unit-btn${unit === 'lbs' ? ' profile-edit__unit-btn--selected' : ''}`}
                    onClick={() => changeWeightUnit('lbs')}
                    aria-pressed={unit === 'lbs'}
                  >
                    {t('basicInfo.unitShortLbs')}
                  </button>
                  <button
                    type="button"
                    className={`profile-edit__unit-btn${unit === 'kg' ? ' profile-edit__unit-btn--selected' : ''}`}
                    onClick={() => changeWeightUnit('kg')}
                    aria-pressed={unit === 'kg'}
                  >
                    {t('basicInfo.unitShortKg')}
                  </button>
                </div>
              </div>
            </div>
            <FormError message={errors.weight} id="update-profile-weight-error" />
            <PrivacySelector value={weightLevel} onChange={setWeightLevel} />
          </div>
          <div className="profile-edit__modal-footer">
            <Button fullWidth onClick={() => setActiveModal(null)}>
              {t('common.buttons.done')}
            </Button>
          </div>
        </Modal>
      )}

      {/* Height modal */}
      {activeModal === 'height' && (
        <Modal
          title={t('profile.updateProfile.labels.height')}
          labelledBy="edit-height-title"
          onClose={() => setActiveModal(null)}
        >
          <div className="profile-edit__modal-body">
            <div className="profile-edit__weight-row">
              <input
                className="profile-edit__weight-input"
                value={height}
                inputMode="decimal"
                placeholder={t('basicInfo.heightPlaceholder')}
                aria-label={t('profile.updateProfile.labels.height')}
                aria-invalid={Boolean(errors.height)}
                aria-describedby={errors.height ? 'update-profile-height-error' : undefined}
                onChange={event => {
                  setHeight(sanitizeWeightInput(event.target.value));
                  clearError('height');
                }}
              />
              <div className="profile-edit__unit-toggle">
                <button
                  type="button"
                  className={`profile-edit__unit-btn${heightUnit === 'ft' ? ' profile-edit__unit-btn--selected' : ''}`}
                  onClick={() => changeHeightUnit('ft')}
                  aria-pressed={heightUnit === 'ft'}
                >
                  {t('basicInfo.unitShortFeet')}
                </button>
                <button
                  type="button"
                  className={`profile-edit__unit-btn${heightUnit !== 'ft' ? ' profile-edit__unit-btn--selected' : ''}`}
                  onClick={() => changeHeightUnit('m')}
                  aria-pressed={heightUnit !== 'ft'}
                >
                  {t('basicInfo.unitShortMeters')}
                </button>
              </div>
            </div>
            <p className="profile-edit__height-readout" aria-live="polite">{heightDisplay}</p>
            <FormError message={errors.height} id="update-profile-height-error" />
            <PrivacySelector value={heightLevel} onChange={setHeightLevel} />
          </div>
          <div className="profile-edit__modal-footer">
            <Button fullWidth onClick={() => setActiveModal(null)}>
              {t('common.buttons.done')}
            </Button>
          </div>
        </Modal>
      )}

      {/* DOB modal */}
      {activeModal === 'dob' && (
        <Modal
          title={t('profile.updateProfile.labels.dateOfBirth')}
          labelledBy="edit-dob-title"
          onClose={closeDobModal}
        >
          <div className="profile-edit__modal-body">
            <ScrollDatePicker
              value={dobDraft ?? dobValue}
              min={minDob}
              max={maxDob}
              ariaLabel={t('profile.updateProfile.labels.dateOfBirth')}
              onChange={setDobDraft}
            />
            <FormError message={errors.dob} />
            <PrivacySelector value={dobLevel} onChange={setDobLevel} />
          </div>
          <div className="profile-edit__modal-footer">
            <Button fullWidth onClick={confirmDob}>
              {t('common.buttons.done')}
            </Button>
          </div>
        </Modal>
      )}
    </section>
  );
}
